import { useEffect, useCallback, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../store';
import { mockMode, fetchAmbientReading, fetchOutdoorWeather, fetchRpiStatus } from '../services/habridge';
import { getOutdoorWeather, getAmbientReading } from '../services/mockData';
import { fetchCurrent, fetchDeviceStatuses, fetchTodayReadings, fetchDailySummaries, fetchAlerts, fetchDeviceEvents, getSourceLabel } from '../services/dataSource';
import { dispatchAlert } from '../services/notificationService';
import type { AlertContext } from '../services/notificationService';
import { connectionLog } from '../services/connectionLog';
import { supabase } from '../services/supabase';
import type { TentConfig } from '../store/settingsSlice';
import { getStageTargets, calculateMetricScore, getScoreLabel, stageToPhase } from '../services/thresholds';
import { deriveClimateMetrics } from '../services/psychrometrics';
import { useAppMetrics } from '../hooks/useAppMetrics';
import { useSensoryFeedback } from '../hooks/useSensoryFeedback';
import { evaluateRules, formatActionLogForTelegram } from '../services/automationEngine';
import { evaluateAllBagRisks, formatContaminationAlert } from '../services/contaminationRisk';
import { sendTelegramMessage } from '../services/notificationService';
import { useMorningBriefing } from '../hooks/useMorningBriefing';
import { useHarvestWindowAlarm } from '../hooks/useHarvestWindowAlarm';
import { useColonizationAlarms } from '../hooks/useColonizationAlarms';
import type { SensorReading, CultivationStage, HealthScore, DeviceStatus, DailySummary, AlertEvent, DeviceEvent, OutdoorWeather, AmbientReading, ClimateSettings } from '../types';
 
export default function SystemDaemon(): null {
  const store = useAppStore(useShallow(s => ({
    climateSettings: s.climateSettings,
    primarySpeciesId: s.primarySpeciesId,
    setPrimarySpeciesId: s.setPrimarySpeciesId,
    setCurrent: s.setCurrent,
    setHealthScore: s.setHealthScore,
    dataSource: s.dataSource,
    setReadings: s.setReadings,
    setOutdoorCurrent: s.setOutdoorCurrent,
    setDevices: s.setDevices,
    setDailySummaries: s.setDailySummaries,
    setAlerts: s.setAlerts,
    setOutdoorWeather: s.setOutdoorWeather,
    setUnreadAlertCount: s.setUnreadAlertCount,
    setAmbientReading: s.setAmbientReading,
    setRpiOnline: s.setRpiOnline,
    setDeviceEvents: s.setDeviceEvents,
    setLastUpdated: s.setLastUpdated,
    setTents: s.setTents,
    current: s.current,
    growBags: s.growBags,
    setSuggestedStage: s.setSuggestedStage,
    healthScore: s.healthScore,
    alerts: s.alerts,
    alertEmailSettings: s.alertEmailSettings,
    alertNotifSettings: s.alertNotifSettings,
    automationEnabled: s.automationEnabled,
    automationRules: s.automationRules,
    updateDevice: s.updateDevice,
    addAutomationLog: s.addAutomationLog,
    readings: s.readings,
    devices: s.devices,
    outdoorCurrent: s.outdoorCurrent,
  })));
  const activeTentId = useAppStore(s => s.activeTentId);
  const tents        = useAppStore(s => s.tents);
  const { activeStage } = useAppMetrics();
  const { triggerWarning, triggerCritical } = useSensoryFeedback();

  const computeHealth = useCallback((reading: SensorReading, speciesId: string, stage: CultivationStage, customSettings?: ClimateSettings): HealthScore => {
    const baseThresholds = getStageTargets(speciesId, stage);
    const s = customSettings || store.climateSettings;
    const thresholds = {
      temperature: { ...baseThresholds.temperature, min: s.temperature.min, max: s.temperature.max, ideal: s.temperature.ideal },
      humidity:    { ...baseThresholds.humidity,    min: s.humidity.min,    max: s.humidity.max,    ideal: s.humidity.ideal },
      co2:         { ...baseThresholds.co2,         min: s.co2.min,         max: s.co2.max,         ideal: s.co2.ideal },
      vpd:             { ...(baseThresholds.vpd ?? { min: 0, max: 3, ideal: 1 }),         min: s.vpd.min,             max: s.vpd.max,             ideal: s.vpd.ideal,             unit: 'kPa' },
      dewPoint:        { ...(baseThresholds.dewPoint ?? { min: 0, max: 30, ideal: 15 }),  min: s.dewPoint.min,        max: s.dewPoint.max,        ideal: s.dewPoint.ideal,        unit: '°C' },
      humidityDeficit: { ...(baseThresholds.humidityDeficit ?? { min: 0, max: 15, ideal: 5 }), min: s.humidityDeficit.min, max: s.humidityDeficit.max, ideal: s.humidityDeficit.ideal, unit: 'g/m³' },
    };
    const derived = deriveClimateMetrics(reading.temperature, reading.humidity);
    const phase = stageToPhase(stage);

    // Phase-aware 3-axis scoring: RH 40% | CO₂ 35% | Temp 25%
    // CO₂ sign flips by phase: fruiting = low CO₂ good; incubation = high CO₂ = mycelium active = good.
    const band = (v: number, lo: number, hi: number, soft: number) =>
      v >= lo && v <= hi ? 100 : Math.max(0, Math.round(100 - (v < lo ? lo - v : v - hi) / soft * 100));

    const humScore  = band(reading.humidity, thresholds.humidity.min, thresholds.humidity.max, (thresholds.humidity.max - thresholds.humidity.min) * 0.8);
    const tempScore = band(reading.temperature, thresholds.temperature.min, thresholds.temperature.max, thresholds.temperature.max - thresholds.temperature.min);
    const co2Score  = phase === 'fruiting'
      ? band(reading.co2, thresholds.co2.min, thresholds.co2.max, thresholds.co2.max * 0.6)
      : Math.min(100, Math.round((reading.co2 / thresholds.co2.ideal) * 100)); // incubation: higher CO₂ = mycelium active

    const overall = Math.round(humScore * 0.40 + co2Score * 0.35 + tempScore * 0.25);

    // Reference axes — kept for display but not part of overall score
    const vpdScore  = calculateMetricScore(derived.vpd,             thresholds.vpd);
    const dpScore   = calculateMetricScore(derived.dewPoint,        thresholds.dewPoint);
    const hdScore   = calculateMetricScore(derived.humidityDeficit, thresholds.humidityDeficit);

    // Condensation risk: surface temp ≈ air temp; T − dewPoint < 2°C && RH > 90
    const condensationRisk = (reading.temperature - derived.dewPoint) < 2 && reading.humidity > 90;

    const { label, color } = getScoreLabel(overall);
    return {
      overall, temperature: tempScore, humidity: humScore, co2: co2Score,
      vpd: vpdScore, dewPoint: dpScore, humidityDeficit: hdScore,
      label: label as HealthScore['label'], color, mode: phase, condensationRisk,
    };
  }, [store.climateSettings]);

  // Initial Data Load — Promise.allSettled ile tek fetch hatası tüm init'i bloklamamaz
  useEffect(() => {
    let cancelled = false;
    async function init() {
      connectionLog.info('DataSource', `Kaynak: ${getSourceLabel()}`);
      const results = await Promise.allSettled([
        fetchCurrent(activeTentId),                                               // 0
        fetchCurrent(tents.find(t => t.isActive && t.id !== activeTentId)?.id ?? activeTentId), // 1 — karşılaştırma çadırı
        fetchDeviceStatuses(),                                                    // 2
        fetchTodayReadings(activeTentId),                                         // 3
        fetchDailySummaries(60, activeTentId),                                    // 4
        fetchAlerts(7),                                                           // 5
        fetchDeviceEvents(7),                                                     // 6
        fetchOutdoorWeather(),                                                    // 7
        fetchAmbientReading(                                                      // 8
          tents.flatMap(t => t.sensors).filter(s => s.isReference && s.haEntityId)
        ),
        fetchRpiStatus(),                                                         // 9
      ]);
      if (cancelled) return;

      function val<T>(i: number, fallback: T): T {
        return results[i].status === 'fulfilled' ? (results[i] as PromiseFulfilledResult<T>).value : fallback;
      }

      const currentReading = val<SensorReading>(0, null as unknown as SensorReading);
      const outdoorReading = val<SensorReading | null>(1, null);
      const deviceList     = val<DeviceStatus[]>(2, []);
      const todayReadings  = val<SensorReading[]>(3, []);
      const rawSummaries   = val<DailySummary[]>(4, []);
      const loadedAlerts   = val<AlertEvent[]>(5, []);
      const deviceEvents   = val<DeviceEvent[]>(6, []);
      const outdoorWeather = val<OutdoorWeather | null>(7, null);
      const ambientReading = val<AmbientReading | null>(8, null);
      const rpiOnline      = val<boolean | null>(9, null);

      // Compute health score for historical summaries (view only stores sensor averages)
      const dailySummaries: DailySummary[] = rawSummaries.map(s => {
        if (s.healthScore > 0) return s;
        const synth = { temperature: s.avgTemp, humidity: s.avgHumidity, co2: s.avgCo2 } as SensorReading;
        const hs = computeHealth(synth, store.primarySpeciesId, store.climateSettings.stage, store.climateSettings);
        return { ...s, healthScore: hs.overall };
      });

      // Hatalı fetch'leri logla ama devam et
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.warn(`[SystemDaemon] init fetch[${i}] başarısız:`, r.reason);
        }
      });

      if (currentReading) {
        store.setCurrent(currentReading);
        store.setHealthScore(computeHealth(currentReading, store.primarySpeciesId, 'fruiting'));
        connectionLog.info('SensorPoll', `[${store.dataSource}] ${todayReadings.length} okuma yüklendi — ${currentReading.temperature}°C / ${currentReading.humidity}% / ${currentReading.co2} ppm`);
      }
      store.setReadings(todayReadings);
      store.setOutdoorCurrent(outdoorReading);
      store.setDevices(deviceList);
      store.setDailySummaries(dailySummaries);
      store.setAlerts(loadedAlerts);
      store.setOutdoorWeather(outdoorWeather ?? getOutdoorWeather());
      store.setUnreadAlertCount(loadedAlerts.length);
      store.setAmbientReading(ambientReading ?? getAmbientReading());
      store.setRpiOnline(rpiOnline);
      store.setDeviceEvents(deviceEvents);
      store.setLastUpdated(new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));
    }
    init();
    return () => { cancelled = true; };
  // activeTentId değiştiğinde veriyi yeniden yükle
  }, [computeHealth, activeTentId]);

  // Subscriptions are now managed in SensorDataProvider to minimize active channels.

  // Tents Supabase sync — startup'ta gerçek tents tablosundan okur
  useEffect(() => {
    if (!supabase) return;
    supabase.from('tents').select('id, name, description, is_active, sort_order, role, location').order('sort_order').then(({ data, error }) => {
      if (error || !data || data.length === 0) return;
      const synced: TentConfig[] = data.map(row => ({
        id: row.id,
        name: row.name,
        description: row.description ?? undefined,
        isActive: row.is_active ?? true,
        sortOrder: row.sort_order ?? 0,
        role: (row.role === 'reference' ? 'reference' : 'primary') as TentConfig['role'],
        location: (row.location ?? 'indoor') as TentConfig['location'],
        sensors: [],
      }));
      store.setTents(synced);
      connectionLog.info('TentSync', `${synced.length} çadır Supabase'den yüklendi`);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watchdog
  const lastReceivedAtRef = useRef<string | null>(null);
  useEffect(() => {
    if (!store.current) return;
    lastReceivedAtRef.current = store.current.receivedAt ?? null;
  }, [store.current]);

  useEffect(() => {
    if (mockMode) return;
    const check = () => {
      const ra = lastReceivedAtRef.current;
      if (!ra) return;
      const ageMin = Math.round((Date.now() - new Date(ra).getTime()) / 60000);
      const wdId = 'sensor-watchdog';
      if (ageMin >= 10) {
        const msg = `Sensör verisi ${ageMin} dakikadır güncellenmiyor — Pi bağlantısı kopmuş olabilir`;
        store.setAlerts((prev: AlertEvent[]) => {
          const exists = prev.find(a => a.id === wdId);
          if (!exists) triggerWarning();
          const entry = { id: wdId, metric: 'sensor' as const, severity: 'warning' as const, message: msg, threshold: 10, value: ageMin, timestamp: new Date().toISOString(), resolved: false };
          return exists ? prev.map(a => a.id === wdId ? entry : a) : [entry, ...prev];
        });
      } else {
        store.setAlerts((prev: AlertEvent[]) => prev.filter(a => a.id !== wdId));
      }
    };
    // 30 saniyede bir kontrol — uyarı gecikmesi < 30s hedefi
    const id = setInterval(check, 30_000);
    return () => clearInterval(id);
  }, [store.setAlerts]);

  // Primary Species & Suggested Stage Computation
  // Stage detection: gün sayısına ek olarak sensör pattern'ı da kullanır
  useEffect(() => {
    const activeBags = store.growBags.filter(b => b.status === 'fruiting' || b.status === 'incubation');
    if (activeBags.length === 0) return;

    // Primary species: en çok torbası olan tür
    const counts: Record<string, number> = {};
    activeBags.forEach(b => { counts[b.speciesId] = (counts[b.speciesId] || 0) + 1; });
    const primary = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (primary) store.setPrimarySpeciesId(primary);

    const incCount = activeBags.filter(b => b.status === 'incubation').length;
    const fruitingBags = activeBags.filter(b => b.status === 'fruiting');

    if (fruitingBags.length === 0 || incCount > fruitingBags.length) {
      store.setSuggestedStage('spawn_run');
      return;
    }

    const now = Date.now();
    const daysList = fruitingBags
      .map(b => b.fruitingStartDate ? (now - new Date(b.fruitingStartDate).getTime()) / 86400000 : 0)
      .filter(d => d > 0);
    const avgDays = daysList.length ? daysList.reduce((a, b) => a + b, 0) / daysList.length : 5;

    // Sensör pattern kontrolü — anlık okumaya göre pinleme koşulu var mı?
    const c = store.current;
    const pinT = getStageTargets(store.primarySpeciesId, 'pinning');
    const sensorSuggestsPinning = c
      ? c.co2 <= pinT.co2.max && c.humidity >= pinT.humidity.min && c.temperature <= pinT.temperature.max
      : false;

    if (sensorSuggestsPinning || avgDays <= 3) {
      store.setSuggestedStage('pinning');
    } else {
      // fruiting until user manually harvests and cycles back to spawn_run
      store.setSuggestedStage('fruiting');
    }
  }, [store.growBags, store.current, store.setPrimarySpeciesId, store.setSuggestedStage]);

  // Ambient + outdoor + RPi polling (5 min)
  useEffect(() => {
    if (mockMode || store.dataSource === 'supabase') return;
    const poll = async () => {
      const refSensors = tents.flatMap(t => t.sensors).filter(s => s.isReference && s.haEntityId);
      const [ambient, outdoor, rpi] = await Promise.all([
        fetchAmbientReading(refSensors),
        fetchOutdoorWeather(),
        fetchRpiStatus(),
      ]);
      if (ambient)       store.setAmbientReading(ambient);
      if (outdoor)       store.setOutdoorWeather(outdoor);
      if (rpi !== null)  store.setRpiOnline(rpi);
    };
    const id = setInterval(poll, 5 * 60_000);
    return () => clearInterval(id);
  }, [tents, store.setAmbientReading, store.setOutdoorWeather, store.setRpiOnline]);

  // Alert Notifications
  const dispatchedAlertIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    const now = Date.now();
    const ctx: AlertContext = { current: store.current, healthScore: store.healthScore?.overall ?? null };
    store.alerts.forEach((alert: AlertEvent) => {
      if (!dispatchedAlertIds.current.has(alert.id)) {
        dispatchedAlertIds.current.add(alert.id);
        const ageMin = (now - new Date(alert.timestamp).getTime()) / 60000;
        if (ageMin <= 30) {
          dispatchAlert(alert, store.alertEmailSettings, store.alertNotifSettings, ctx);
        }
      }
    });
  }, [store.alerts, store.alertEmailSettings, store.alertNotifSettings, store.current, store.healthScore]);

  // Health Score Calculation Loop
  useEffect(() => {
    if (store.current) {
      store.setHealthScore(computeHealth(store.current, store.primarySpeciesId, activeStage, store.climateSettings));
    }
  }, [store.primarySpeciesId, activeStage, store.current, computeHealth, store.climateSettings, store.setHealthScore]);

  // Pinleme otomatik tespiti — eşikler getStageTargets('pinning') kaynağından
  const pinDetectRef = useRef<string | null>(null);
  useEffect(() => {
    const c = store.current;
    if (!c) return;
    const pinId = 'auto-pin-detect';
    const pt = getStageTargets(store.primarySpeciesId, 'pinning');
    const isPinCondition = c.co2 <= pt.co2.max && c.humidity >= pt.humidity.min && c.temperature <= pt.temperature.max;
    if (isPinCondition && pinDetectRef.current !== 'triggered') {
      pinDetectRef.current = 'triggered';
      store.setAlerts((prev: AlertEvent[]) => {
        if (prev.find(a => a.id === pinId && !a.resolved)) return prev;
        const entry = {
          id: pinId, metric: 'humidity' as const, severity: 'info' as const,
          message: `Pinleme koşulları oluştu — CO₂ ${c.co2} ppm, nem %${c.humidity}, sıcaklık ${c.temperature}°C. Primordia oluşumu başlayabilir.`,
          threshold: 88, value: c.humidity, timestamp: new Date().toISOString(), resolved: false,
        };
        return [entry, ...prev];
      });
    } else if (!isPinCondition && pinDetectRef.current === 'triggered') {
      pinDetectRef.current = null;
      store.setAlerts((prev: AlertEvent[]) => prev.map(a => a.id === pinId ? { ...a, resolved: true } : a));
    }
  }, [store.current, store.setAlerts]);

  // Kontaminasyon erken uyarısı — Anlık spike tespiti
  // Kural: CO₂ ani yükselişi (30 dk içinde > 300 ppm artış) + nem ani düşüşü (> 10% düşüş)
  const prevReadingRef = useRef<SensorReading | null>(null);
  useEffect(() => {
    const c = store.current;
    if (!c || !prevReadingRef.current) {
      prevReadingRef.current = c;
      return;
    }
    const prev = prevReadingRef.current;
    const co2Spike = c.co2 - prev.co2;
    const humDrop  = prev.humidity - c.humidity;
    const contamId = 'contamination-risk';
    if (co2Spike > 300 && humDrop > 8) {
      store.setAlerts((prev2: AlertEvent[]) => {
        if (prev2.find(a => a.id === contamId && !a.resolved)) return prev2;
        triggerCritical();
        const entry = {
          id: contamId, metric: 'co2' as const, severity: 'warning' as const,
          message: `Kontaminasyon riski — CO₂ ${co2Spike > 0 ? '+' : ''}${co2Spike} ppm artış, nem %${humDrop} düşüş tespit edildi. Torbalar görsel olarak kontrol edilmeli.`,
          threshold: 300, value: co2Spike, timestamp: new Date().toISOString(), resolved: false,
        };
        return [entry, ...prev2];
      });
    }
    prevReadingRef.current = c;
  }, [store.current, store.setAlerts]);

  // Kontaminasyon erken uyarısı — Z-skoru tabanlı rolling window tespiti
  // Sensör boşluklarında (gap > 90 dk) pencere sıfırlanır — tutarsız istatistik önlenir
  const co2WindowRef  = useRef<number[]>([]);
  const humWindowRef  = useRef<number[]>([]);
  const lastReadingTs = useRef<number>(0);

  useEffect(() => {
    const c = store.current;
    if (!c) return;

    const WINDOW_SIZE  = 24;
    const MIN_READINGS = 8;
    const GAP_LIMIT_MS = 90 * 60 * 1000; // 90 dakika boşluk → pencere sıfırla

    // Sensör timestamp varsa gap kontrolü yap
    const now = Date.now();
    const readingTime = c.receivedAt ? new Date(c.receivedAt).getTime() : now;
    if (lastReadingTs.current > 0 && readingTime - lastReadingTs.current > GAP_LIMIT_MS) {
      co2WindowRef.current = [];
      humWindowRef.current = [];
      console.info('[ZScore] Sensör boşluğu tespit edildi, pencere sıfırlandı');
    }
    lastReadingTs.current = readingTime;

    // Push to windows and trim
    co2WindowRef.current = [...co2WindowRef.current, c.co2].slice(-WINDOW_SIZE);
    humWindowRef.current = [...humWindowRef.current, c.humidity].slice(-WINDOW_SIZE);

    const co2Window = co2WindowRef.current;
    const humWindow = humWindowRef.current;
    const windowSize = co2Window.length;

    if (windowSize < MIN_READINGS) return;

    const mean = (arr: number[]) => arr.reduce((s, v) => s + v, 0) / arr.length;
    const stdDev = (arr: number[], m: number) =>
      Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length);

    const co2Mean = mean(co2Window);
    const rawCo2Std = stdDev(co2Window, co2Mean);
    const co2Std  = Math.max(rawCo2Std, 30.0); // CO2 için 30 ppm alt sınır koruması

    const humMean = mean(humWindow);
    const rawHumStd = stdDev(humWindow, humMean);
    const humStd  = Math.max(rawHumStd, 1.0);  // Nem için %1.0 alt sınır koruması

    const co2Z = (c.co2 - co2Mean) / co2Std;
    const humZ = (c.humidity - humMean) / humStd;

    const isCo2Anomaly  = co2Z > 2.0;
    const isHumAnomaly  = humZ < -1.5;

    if (!isCo2Anomaly && !isHumAnomaly) return;

    // Confirm: at least 2 of last 3 readings show the same anomaly (not a single spike)
    const last3Co2 = co2Window.slice(-3);
    const last3Hum = humWindow.slice(-3);
    const co2AnomalyCount = last3Co2.filter(v => (v - co2Mean) / co2Std > 2.0).length;
    const humAnomalyCount = last3Hum.filter(v => (v - humMean) / humStd < -1.5).length;

    const confirmedCo2 = isCo2Anomaly && co2AnomalyCount >= 2;
    const confirmedHum = isHumAnomaly && humAnomalyCount >= 2;

    if (confirmedCo2) {
      const co2ZScoreId = 'zscore-contamination-co2';
      const latestCo2 = co2Window[co2Window.length - 1] ?? 0;
      store.setAlerts((prev: AlertEvent[]) => {
        if (prev.find(a => a.id === co2ZScoreId && !a.resolved)) return prev;
        triggerCritical();
        const entry = {
          id: co2ZScoreId,
          metric: 'co2' as const,
          severity: 'warning' as const,
          message: `CO₂ Z-skoru ${co2Z.toFixed(1)} — ${windowSize} okumalık norm üzerinde anormal yükseliş. Kontaminasyon riski (48-72 saat önceden tespit).`,
          threshold: 2,
          value: Math.round(latestCo2),
          timestamp: new Date().toISOString(),
          resolved: false,
        };
        return [entry, ...prev];
      });
    }

    if (confirmedHum) {
      const humZScoreId = 'zscore-contamination-humidity';
      const latestHum = humWindow[humWindow.length - 1] ?? 0;
      store.setAlerts((prev: AlertEvent[]) => {
        if (prev.find(a => a.id === humZScoreId && !a.resolved)) return prev;
        triggerCritical();
        const entry = {
          id: humZScoreId,
          metric: 'humidity' as const,
          severity: 'warning' as const,
          message: `Nem Z-skoru ${humZ.toFixed(1)} — ${windowSize} okumalık norm altında anormal nem düşüşü.`,
          threshold: -1.5,
          value: Math.round(latestHum),
          timestamp: new Date().toISOString(),
          resolved: false,
        };
        return [entry, ...prev];
      });
    }
  }, [store.current, store.setAlerts]);

  // ── Feature 1: Automation Engine — sensör okuması geldiğinde kuralları değerlendir
  useEffect(() => {
    const c = store.current;
    if (!c || !store.automationEnabled) return;

    evaluateRules({
      reading: c,
      devices: store.devices,
      activeStage,
      activeTentId,
      rules: store.automationRules,
      onDeviceToggle: (deviceId, turnOn) => {
        store.updateDevice(deviceId, { isActive: turnOn, lastToggled: new Date().toISOString() });
      },
      onLog: (log) => {
        store.addAutomationLog(log);
        // Telegram'a bildir
        const msg = formatActionLogForTelegram(log);
        sendTelegramMessage(msg).catch(() => {});
        connectionLog.info('Automation', `${log.ruleName}: ${log.deviceName} ${log.action === 'turn_on' ? 'açıldı' : 'kapatıldı'}`);
      },
      onAlarm: (message) => {
        triggerWarning();
        connectionLog.warn('Automation', message);
      },
    });
  }, [store.current, store.automationEnabled, store.automationRules, store.devices, activeStage, activeTentId]);

  // ── Feature 4: Kontaminasyon Risk Skoru — her 10 dakikada bir torbaları değerlendir
  const lastContamCheckRef = useRef<number>(0);
  const sentContamAlertBags = useRef<Set<string>>(new Set());
  useEffect(() => {
    const now = Date.now();
    if (now - lastContamCheckRef.current < 10 * 60_000) return; // 10 dk'da bir
    lastContamCheckRef.current = now;

    const risks = evaluateAllBagRisks(store.growBags, store.current, store.readings);
    const highRisks = risks.filter(r => r.riskLevel === 'high' || r.riskLevel === 'critical');

    for (const risk of highRisks) {
      // Aynı torba için tekrar alarm gönderme (session boyunca)
      if (sentContamAlertBags.current.has(risk.bagId)) continue;
      sentContamAlertBags.current.add(risk.bagId);

      const alertId = `contam-risk-${risk.bagId}`;
      store.setAlerts((prev: AlertEvent[]) => {
        if (prev.find(a => a.id === alertId && !a.resolved)) return prev;
        if (risk.riskLevel === 'critical') triggerCritical();
        else triggerWarning();
        return [{
          id: alertId,
          metric: 'co2' as const,
          severity: risk.riskLevel === 'critical' ? 'critical' as const : 'warning' as const,
          message: risk.recommendation,
          threshold: 60,
          value: risk.riskScore,
          timestamp: new Date().toISOString(),
          resolved: false,
        }, ...prev];
      });

      // Telegram bildirim
      const tgMsg = formatContaminationAlert(risk);
      sendTelegramMessage(tgMsg).catch(() => {});
    }
  }, [store.current, store.growBags, store.readings]);

  // ── Feature 2: Sabah Aksiyon Bülteni — hook olarak çağır
  useMorningBriefing({
    bags: store.growBags,
    alerts: store.alerts,
    todayReadings: store.readings,
    current: store.current,
    devices: store.devices,
    healthScore: store.healthScore?.overall ?? 0,
    notifSettings: store.alertNotifSettings,
  });

  // ── Feature 2b: Aslan Yelesi hasat penceresi gerçek zamanlı alarmı (12-24h)
  useHarvestWindowAlarm({ notifSettings: store.alertNotifSettings });

  // ── Feature 2c: İnokulasyon risk penceresi + milestone alarmları (B5+B7)
  useColonizationAlarms({ notifSettings: store.alertNotifSettings });

  // ── Feature 2d: Tent-2 → Tent-1 önceden soğut uyarısı (B6)
  // Lion's Mane fruiting max 24°C. Tent-2 (outdoor) >28°C = Tent-1 risk içinde.
  useEffect(() => {
    const outdoorTemp = store.outdoorCurrent?.temperature;
    if (!outdoorTemp || !store.alertNotifSettings.telegram?.enabled) return;
    if (outdoorTemp < 28) return;

    const today = new Date().toISOString().slice(0, 10);
    const key = `gmc_tent2_preheat_${today}`;
    if (localStorage.getItem(key)) return;

    const lines = [
      `🌡️ <b>TENT-2 YÜKSEK — TENT-1 ÖNLEM AL</b>`,
      ``,
      `Tent-2 (dış ortam): <b>${outdoorTemp.toFixed(1)}°C</b>`,
      `Aslan Yelesi fruiting max: <b>24°C</b>`,
      ``,
      `⚡ Önerilen aksiyonlar:`,
      `• Soğutucuyu şimdi aç / set point'i düşür`,
      `• Havalandırmayı gece saatlerine kaydır`,
      `• Substrat öz ısısı hava sıcaklığından +2°C olabilir`,
    ];

    localStorage.setItem(key, '1');
    sendTelegramMessage(lines.join('\n')).catch(() => {});
  }, [store.outdoorCurrent, store.alertNotifSettings]);

  // ── Feature 2e: Cold Chain Notification on Harvested Bags ──────────────────
  const prevBagsStatusRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!store.alertNotifSettings.telegram?.enabled) return;
    for (const bag of store.growBags) {
      const prevStatus = prevBagsStatusRef.current[bag.id];
      if (prevStatus && prevStatus !== 'harvested' && bag.status === 'harvested') {
        const lines = [
          `❄️ <b>SOĞUK ZİNCİR UYARISI — ${bag.name} HASAT EDİLDİ!</b>`,
          ``,
          `Hasat edilen torbayı <b>HEMEN 0-4°C'ye al</b>.`,
          `⚠️ Bekleme yapılması durumunda <b>tyrosinase enzimi aktive olur</b> → sararma başlar ve bioactive bileşen kalitesi düşer.`,
        ];
        sendTelegramMessage(lines.join('\n')).catch(() => {});
      }
      prevBagsStatusRef.current[bag.id] = bag.status;
    }
  }, [store.growBags, store.alertNotifSettings.telegram?.enabled]);

  return null;
}
